import * as THREE from "three";
import { CATEGORIES, CATEGORY_META, mixCategoryColor, primaryCategory } from "./categories.js";
import { buildLinks } from "./layout.js";

const DRIFT_GLSL = `
vec3 drift(vec3 p, float phase, float time) {
  return p + vec3(
    sin(time * 0.13 + phase) * 0.92,
    cos(time * 0.11 + phase * 1.7) * 0.68,
    sin(time * 0.09 + phase * 0.61) * 0.88
  );
}
`;

const POINT_VERTEX = `
uniform float uTime;
uniform float uPixelRatio;
uniform float uScale;
attribute float aSize;
attribute float aPhase;
attribute float aEntropy;
attribute float aVisible;
attribute vec3 aColor;
varying vec3 vColor;
varying float vEntropy;
varying float vVisible;
${DRIFT_GLSL}
void main() {
  vColor = aColor;
  vEntropy = aEntropy;
  vVisible = aVisible;
  if (aVisible < 0.5) {
    gl_PointSize = 0.0;
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    return;
  }
  vec3 drifted = drift(position, aPhase, uTime);
  vec4 mv = modelViewMatrix * vec4(drifted, 1.0);
  gl_PointSize = aSize * uScale * uPixelRatio * (180.0 / max(40.0, -mv.z));
  gl_Position = projectionMatrix * mv;
}
`;

const POINT_FRAGMENT = `
uniform vec3 uWash;
varying vec3 vColor;
varying float vEntropy;
varying float vVisible;
void main() {
  if (vVisible < 0.5) discard;
  vec2 p = gl_PointCoord * 2.0 - 1.0;
  float d = length(p);
  if (d > 1.0) discard;
  float core = smoothstep(1.0, 0.12, d);
  float halo = smoothstep(1.0, 0.0, d) * 0.35;
  float alpha = core * 0.95 + halo;
  vec3 col = mix(vColor, uWash, 0.08 + vEntropy * 0.16);
  gl_FragColor = vec4(col, alpha);
}
`;

const LINE_VERTEX = `
uniform float uTime;
uniform float uLineAlpha;
attribute float aPhase;
attribute float aVisible;
attribute vec3 aColor;
varying vec3 vColor;
varying float vAlpha;
${DRIFT_GLSL}
void main() {
  vColor = aColor;
  vAlpha = uLineAlpha * aVisible;
  vec3 drifted = drift(position, aPhase, uTime);
  gl_Position = projectionMatrix * modelViewMatrix * vec4(drifted, 1.0);
}
`;

const LINE_FRAGMENT = `
varying vec3 vColor;
varying float vAlpha;
void main() {
  if (vAlpha < 0.002) discard;
  gl_FragColor = vec4(vColor, vAlpha);
}
`;

const GRID_VERTEX = `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const GRID_FRAGMENT = `
uniform vec3 uGridColor;
uniform float uGridAlpha;
varying vec2 vUv;
void main() {
  vec2 g = abs(fract(vUv * 28.0) - 0.5);
  float line = 1.0 - min(g.x, g.y) * 2.0;
  float grid = smoothstep(0.88, 1.0, line);
  float fade = 1.0 - smoothstep(0.15, 0.72, length(vUv - 0.5));
  float alpha = grid * fade * uGridAlpha;
  gl_FragColor = vec4(uGridColor, alpha);
}
`;

export function createGalaxy(rows, layout) {
  const group = new THREE.Group();
  const { positions, phases, sizes, entropy } = layout;
  const count = rows.length;
  const colors = new Float32Array(count * 3);
  const visible = new Float32Array(count);
  visible.fill(1);
  const rgb = { r: 0, g: 0, b: 0, set(r, g, b) { this.r = r; this.g = g; this.b = b; } };

  paintPointColors(rows, colors, rgb, null);

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute("aColor", new THREE.BufferAttribute(colors, 3));
  geometry.setAttribute("aSize", new THREE.BufferAttribute(sizes, 1));
  geometry.setAttribute("aPhase", new THREE.BufferAttribute(phases, 1));
  geometry.setAttribute("aEntropy", new THREE.BufferAttribute(entropy, 1));
  geometry.setAttribute("aVisible", new THREE.BufferAttribute(visible, 1));
  geometry.computeBoundingSphere();

  const pointMaterial = new THREE.ShaderMaterial({
    uniforms: {
      uTime: { value: 0 },
      uPixelRatio: { value: Math.min(window.devicePixelRatio, 1.75) },
      uScale: { value: 1 },
      uWash: { value: new THREE.Vector3(0.92, 0.96, 1.0) },
    },
    vertexShader: POINT_VERTEX,
    fragmentShader: POINT_FRAGMENT,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });

  const points = new THREE.Points(geometry, pointMaterial);
  points.frustumCulled = false;
  group.add(points);

  const pairs = buildLinks(rows, positions, entropy, 3);
  const lineCount = pairs.length / 2;
  const linePos = new Float32Array(lineCount * 6);
  const lineColor = new Float32Array(lineCount * 6);
  const linePhase = new Float32Array(lineCount * 2);
  const lineVisible = new Float32Array(lineCount * 2);
  lineVisible.fill(1);
  writeLineGeometry(pairs, positions, colors, phases, linePos, lineColor, linePhase);

  const lineGeometry = new THREE.BufferGeometry();
  lineGeometry.setAttribute("position", new THREE.BufferAttribute(linePos, 3));
  lineGeometry.setAttribute("aColor", new THREE.BufferAttribute(lineColor, 3));
  lineGeometry.setAttribute("aPhase", new THREE.BufferAttribute(linePhase, 1));
  lineGeometry.setAttribute("aVisible", new THREE.BufferAttribute(lineVisible, 1));
  const lineMaterial = new THREE.ShaderMaterial({
    uniforms: {
      uTime: { value: 0 },
      uLineAlpha: { value: 0.16 },
    },
    vertexShader: LINE_VERTEX,
    fragmentShader: LINE_FRAGMENT,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  const lines = new THREE.LineSegments(lineGeometry, lineMaterial);
  lines.frustumCulled = false;
  group.add(lines);

  const starfield = createStarfield();
  const grid = createGrid();
  const glows = createClusterGlows(layout.centers);
  group.add(starfield.points);
  group.add(grid);
  group.add(glows.group);
  const marker = createFocusMarker();
  group.add(marker);

  function driftedPosition(index, time, target) {
    const phase = phases[index];
    const x = positions[index * 3];
    const y = positions[index * 3 + 1];
    const z = positions[index * 3 + 2];
    target.set(
      x + Math.sin(time * 0.13 + phase) * 0.92,
      y + Math.cos(time * 0.11 + phase * 1.7) * 0.68,
      z + Math.sin(time * 0.09 + phase * 0.61) * 0.88,
    );
    return target;
  }

  function setTime(time) {
    pointMaterial.uniforms.uTime.value = time;
    lineMaterial.uniforms.uTime.value = time;
  }

  function setPixelRatio(ratio) {
    pointMaterial.uniforms.uPixelRatio.value = Math.min(ratio, 1.75);
  }

  function applyTheme(theme, wordRows) {
    paintPointColors(wordRows, colors, rgb, theme.categoryColors);
    geometry.getAttribute("aColor").needsUpdate = true;
    writeLineGeometry(pairs, positions, colors, phases, linePos, lineColor, linePhase);
    lineGeometry.getAttribute("aColor").needsUpdate = true;
    pointMaterial.uniforms.uWash.value.set(theme.wash[0], theme.wash[1], theme.wash[2]);
    pointMaterial.uniforms.uScale.value = theme.pointScale;
    lineMaterial.uniforms.uLineAlpha.value = theme.lineAlpha;
    grid.material.uniforms.uGridColor.value.set(theme.grid[0], theme.grid[1], theme.grid[2]);
    grid.material.uniforms.uGridAlpha.value = theme.gridAlpha;
    marker.material.color.setHex(theme.marker);
    starfield.apply(theme.stars);
    glows.apply(theme.categoryColors);
  }

  function setCategoryFilter(wordRows, activeSet) {
    const showAll = !activeSet || activeSet.size === 0 || activeSet.size === CATEGORIES.length;
    for (let i = 0; i < wordRows.length; i += 1) {
      if (showAll) {
        visible[i] = 1;
        continue;
      }
      visible[i] = activeSet.has(primaryCategory(wordRows[i].scores)) ? 1 : 0;
    }
    geometry.getAttribute("aVisible").needsUpdate = true;
    for (let e = 0; e < lineCount; e += 1) {
      const a = pairs[e * 2];
      const b = pairs[e * 2 + 1];
      const vis = visible[a] * visible[b];
      lineVisible[e * 2] = vis;
      lineVisible[e * 2 + 1] = vis;
    }
    lineGeometry.getAttribute("aVisible").needsUpdate = true;
    glows.filter(activeSet, showAll);
  }

  return {
    group,
    points,
    lines,
    positions,
    phases,
    sizes,
    entropy,
    colors,
    visible,
    centers: layout.centers,
    driftedPosition,
    setTime,
    setPixelRatio,
    applyTheme,
    setCategoryFilter,
    marker,
  };
}

function paintPointColors(rows, colors, rgb, colorMap) {
  for (let i = 0; i < rows.length; i += 1) {
    mixCategoryColor(rows[i].scores, rgb, colorMap);
    colors[i * 3] = rgb.r / 255;
    colors[i * 3 + 1] = rgb.g / 255;
    colors[i * 3 + 2] = rgb.b / 255;
  }
}

function writeLineGeometry(pairs, positions, colors, phases, linePos, lineColor, linePhase) {
  const lineCount = pairs.length / 2;
  for (let e = 0; e < lineCount; e += 1) {
    const a = pairs[e * 2];
    const b = pairs[e * 2 + 1];
    linePos[e * 6] = positions[a * 3];
    linePos[e * 6 + 1] = positions[a * 3 + 1];
    linePos[e * 6 + 2] = positions[a * 3 + 2];
    linePos[e * 6 + 3] = positions[b * 3];
    linePos[e * 6 + 4] = positions[b * 3 + 1];
    linePos[e * 6 + 5] = positions[b * 3 + 2];
    lineColor[e * 6] = (colors[a * 3] + colors[b * 3]) * 0.5;
    lineColor[e * 6 + 1] = (colors[a * 3 + 1] + colors[b * 3 + 1]) * 0.5;
    lineColor[e * 6 + 2] = (colors[a * 3 + 2] + colors[b * 3 + 2]) * 0.5;
    lineColor[e * 6 + 3] = lineColor[e * 6];
    lineColor[e * 6 + 4] = lineColor[e * 6 + 1];
    lineColor[e * 6 + 5] = lineColor[e * 6 + 2];
    linePhase[e * 2] = phases[a];
    linePhase[e * 2 + 1] = phases[b];
  }
}

function createFocusMarker() {
  const geo = new THREE.RingGeometry(0.62, 0.82, 48);
  const mat = new THREE.MeshBasicMaterial({
    color: 0xe8c47a,
    transparent: true,
    opacity: 0.95,
    side: THREE.DoubleSide,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.visible = false;
  mesh.renderOrder = 2;
  return mesh;
}

function createStarfield() {
  const n = 7000;
  const pos = new Float32Array(n * 3);
  const col = new Float32Array(n * 3);
  const shades = new Float32Array(n);
  for (let i = 0; i < n; i += 1) {
    const r = 220 + Math.random() * 420;
    const u = Math.random();
    const v = Math.random();
    const theta = 2 * Math.PI * u;
    const phi = Math.acos(2 * v - 1);
    pos[i * 3] = r * Math.sin(phi) * Math.cos(theta);
    pos[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta) * 0.72;
    pos[i * 3 + 2] = r * Math.cos(phi);
    shades[i] = 0.28 + Math.random() * 0.72;
    col[i * 3] = shades[i] * 0.82;
    col[i * 3 + 1] = shades[i] * 0.88;
    col[i * 3 + 2] = shades[i];
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
  geo.setAttribute("color", new THREE.BufferAttribute(col, 3));
  const mat = new THREE.PointsMaterial({
    size: 0.9,
    vertexColors: true,
    transparent: true,
    opacity: 0.7,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    sizeAttenuation: true,
  });
  const points = new THREE.Points(geo, mat);
  points.frustumCulled = false;
  function apply(star) {
    const attr = geo.getAttribute("color");
    for (let i = 0; i < n; i += 1) {
      const shade = shades[i] * star.brightness;
      attr.array[i * 3] = shade * star.tint[0];
      attr.array[i * 3 + 1] = shade * star.tint[1];
      attr.array[i * 3 + 2] = shade * star.tint[2];
    }
    attr.needsUpdate = true;
    mat.size = star.size;
    mat.opacity = star.opacity;
  }
  return { points, apply };
}

function createGrid() {
  const geo = new THREE.PlaneGeometry(180, 180, 1, 1);
  geo.rotateX(-Math.PI / 2);
  const mat = new THREE.ShaderMaterial({
    uniforms: {
      uGridColor: { value: new THREE.Vector3(0.55, 0.72, 0.95) },
      uGridAlpha: { value: 0.18 },
    },
    vertexShader: GRID_VERTEX,
    fragmentShader: GRID_FRAGMENT,
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    blending: THREE.AdditiveBlending,
  });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.position.y = 0.2;
  mesh.rotation.y = Math.PI * 0.18;
  mesh.renderOrder = -1;
  return mesh;
}

function createClusterGlows(centers) {
  const group = new THREE.Group();
  const geo = new THREE.SphereGeometry(6.5, 16, 16);
  const meshes = {};
  for (const key of CATEGORIES) {
    const c = centers[key];
    const color = new THREE.Color(CATEGORY_META[key].color);
    const mat = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.035,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(c.x, c.y, c.z);
    mesh.scale.set(1.6, 1.05, 1.6);
    group.add(mesh);
    meshes[key] = mesh;
  }
  function apply(colorMap) {
    for (const key of CATEGORIES) {
      meshes[key].material.color.set(colorMap[key] || CATEGORY_META[key].color);
    }
  }
  function filter(activeSet, showAll) {
    for (const key of CATEGORIES) {
      const on = showAll || (activeSet && activeSet.has(key));
      meshes[key].material.opacity = on ? 0.035 : 0.0;
    }
  }
  return { group, apply, filter };
}
